import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, User } from 'lucide-react';
import { business } from '@/config/business';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  let servicesTimeout: NodeJS.Timeout;

  const handleServicesMouseEnter = () => {
    clearTimeout(servicesTimeout);
    setServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    servicesTimeout = setTimeout(() => {
      setServicesOpen(false);
    }, 200);
  };

  const nashvilleServices = [
    { name: 'Matterport 3D Tours', path: '/matterport-services-nashville-tn', description: 'Immersive virtual property tours' },
    { name: 'Insurance Documentation', path: '/insurance-documentation-nashville-tn', description: 'Pre-loss & claims support' },
    { name: 'Construction Documentation', path: '/construction-documentation-nashville-tn', description: 'Progress tracking & as-builts' },
    { name: 'Floor Plans & Measurements', path: '/real-estate-3d-tours-nashville-tn', description: 'Survey-grade accuracy' },
  ];

  const links = [
    { name: 'Industries', path: '/industries' },
    { name: 'Enterprise', path: '/enterprise' },
    { name: 'Nashville', path: '/service-areas' },
    { name: 'Pricing', path: '/pricing' },
  ];

  const linkClass = (path: string) =>
    `py-7 border-b-2 text-[0.68rem] font-extrabold uppercase tracking-[0.1em] transition-colors ${
      isActive(path) ? 'border-primary text-primary' : 'border-transparent text-foreground hover:border-primary'
    }`;

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background">
      <div className="flex min-h-[80px] items-center justify-between gap-6 px-5 md:px-8 lg:px-12">
        <Link to="/" className="flex items-center" aria-label="TourMy.co home">
          <img
            src="/lovable-uploads/584c7a53-6ce0-45a6-ae2b-a2fdaed98442.png"
            alt="TourMy.co Logo"
            className="h-12 w-auto object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Link>

        {/* Desktop navigation */}
        <div className="hidden items-center gap-8 lg:flex">
          <div
            className="relative"
            onMouseEnter={handleServicesMouseEnter}
            onMouseLeave={handleServicesMouseLeave}
          >
            <button
              className={`${linkClass('/services')} flex items-center gap-1`}
              aria-expanded={servicesOpen}
              aria-controls="desktop-services-menu"
              onClick={() => setServicesOpen((open) => !open)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setServicesOpen(false);
              }}
            >
              Services <ChevronDown className="h-3 w-3" />
            </button>
            {servicesOpen && (
              <div
                id="desktop-services-menu"
                className="absolute left-0 top-full z-50 w-80 border border-border bg-background"
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
              >
                <div className="p-5">
                  <div className="ed-kicker mb-4">Nashville Services</div>
                  <div className="divide-y divide-border border-y border-border">
                    {nashvilleServices.map((service) => (
                      <Link
                        key={service.name}
                        to={service.path}
                        className="group block py-3"
                        onClick={() => setServicesOpen(false)}
                      >
                        <div className="font-serif text-lg leading-tight transition-colors group-hover:text-primary">
                          {service.name}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{service.description}</div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/services"
                    className="mt-5 inline-flex items-center gap-2 text-[0.68rem] font-extrabold uppercase tracking-[0.1em] text-primary"
                    onClick={() => setServicesOpen(false)}
                  >
                    View all services →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {links.map((link) => (
            <Link key={link.path} to={link.path} className={linkClass(link.path)}>
              {link.name}
            </Link>
          ))}

          <Link to="/portal/login" className={`${linkClass('/portal/login')} flex items-center gap-1.5`}>
            <User className="h-3.5 w-3.5" />
            Portal
          </Link>

          <a
            href={business.phone.href}
            aria-label={`Call TourMy at ${business.phone.display}`}
            className="inline-flex min-h-[48px] items-center gap-3 border border-primary bg-primary px-4 text-[0.7rem] font-extrabold uppercase tracking-[0.09em] text-primary-foreground"
          >
            <Phone className="h-4 w-4" />
            {business.phone.display}
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          className="inline-flex min-h-11 min-w-11 items-center justify-center lg:hidden"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
          aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div id="mobile-navigation" className="border-t border-border px-5 py-4 lg:hidden">
          <div className="flex flex-col divide-y divide-border border-y border-border">
            {[{ name: 'Services', path: '/services' }, ...links, { name: 'Client Portal', path: '/portal/login' }].map(
              (link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="py-3 text-[0.72rem] font-extrabold uppercase tracking-[0.1em]"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>
          <a
            href={business.phone.href}
            className="mt-4 inline-flex w-full min-h-[52px] items-center justify-center gap-3 bg-primary px-4 text-[0.72rem] font-extrabold uppercase tracking-[0.09em] text-primary-foreground"
          >
            <Phone className="h-4 w-4" />
            Call {business.phone.display}
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
